/**
 * Настройки → Интеграции (docs/08-screens.md).
 *
 * Состояние каждого источника видно здесь, а не только в логах на сервере:
 * должно быть понятно, почему баланс не обновился (docs/11-nonfunctional.md).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Dialog, Icon, Select } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { Provenance } from '@/components/money';
import { ApiError } from '@/api/client';
import {
  useIntegrations,
  useOperation,
  type IntegrationRow,
  type SyncRunResult,
} from '@/api/hooks';
import { formatRelative, plural } from '@/lib/format';
import { useControlSize } from '@/lib/useControlSize';
import { QueryState } from '@/components/QueryState';
import { RatesCard } from './RatesCard';
import { WalletSourcesCard } from './WalletSourcesCard';

const DEPTH_OPTIONS = [
  { value: '30', label: 'Месяц' },
  { value: '90', label: 'Три месяца' },
  { value: '180', label: 'Полгода' },
  { value: '365', label: 'Год' },
];

function statusBadge(row: IntegrationRow) {
  if (!row.supportsSync) return <Badge tone="neutral">Вручную</Badge>;
  if (row.services.length === 0) return <Badge tone="neutral">Сервисов нет</Badge>;
  if (row.failureStreak >= 3) return <Badge tone="danger" dot>Падает подряд</Badge>;
  if (row.error) return <Badge tone="warning" dot>Последний запуск с ошибкой</Badge>;
  if (row.lastSuccessAt) return <Badge tone="success" dot>Работает</Badge>;
  return <Badge tone="neutral">Не запускалась</Badge>;
}

function ServiceState({
  service,
  row,
  onOpen,
  onSync,
  onBackfill,
  busy,
}: {
  service: IntegrationRow['services'][number];
  row: IntegrationRow;
  onOpen: () => void;
  onSync: () => void;
  onBackfill: () => void;
  busy: boolean;
}) {
  return (
    <div
      style={{
        paddingTop: 'var(--space-4)',
        borderTop: '1px solid var(--border-subtle)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--space-4)',
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ font: '500 14px var(--font-text)', color: 'var(--text-strong)' }}>
            {service.name}
          </span>
          {service.isActive ? null : <Badge tone="neutral">Отключён</Badge>}
          {!row.needsCredential ? null : service.hasCredential ? (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-subtle)' }}>
              {service.maskedHint}
            </span>
          ) : (
            <Badge tone="warning">Ключ не задан</Badge>
          )}
        </div>

        <div style={{ marginTop: 'var(--space-2)' }}>
          <Provenance>
            {!row.supportsSync
              ? 'Расход вносится вручную: публичного API биллинга у этого источника нет'
              : service.lastSyncStatus === 'failed' && service.lastSyncError
                ? service.lastSyncError
                : service.lastSyncAt
                  ? `Синхронизация ${formatRelative(service.lastSyncAt)}`
                  : 'Синхронизация ещё не запускалась'}
          </Provenance>
          <Provenance>
            {service.usageRecords > 0
              ? `Записей расхода ${service.usageRecords}` +
                (service.usageThroughDate ? `, данные по ${service.usageThroughDate}` : '')
              : 'Расход ещё не собран'}
          </Provenance>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        {row.supportsSync ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={onSync}
              disabled={busy || !service.hasCredential}
            >
              Синхронизировать
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={onBackfill}
              disabled={busy || !service.hasCredential}
            >
              Импортировать историю
            </Button>
          </>
        ) : null}
        <Button variant="ghost" size="sm" onClick={onOpen}>
          Открыть
        </Button>
      </div>
    </div>
  );
}

export function IntegrationsTab() {
  const navigate = useNavigate();
  const size = useControlSize();
  const integrations = useIntegrations();
  const sync = useOperation<{ serviceId?: string }, { results: SyncRunResult[] }>('sync.run');
  const backfill = useOperation<
    { serviceId: string; days: number },
    { status: string; usageRecords: number; invoices: number; days: number; error: string | null }
  >('sync.backfill');

  const [backfillFor, setBackfillFor] = useState<{ id: string; name: string } | null>(null);
  const [depth, setDepth] = useState('180');
  const [message, setMessage] = useState<string | null>(null);

  const rows = integrations.data?.integrations ?? [];
  const withServices = rows.filter((row) => row.services.length > 0);

  function runSync(serviceId?: string) {
    setMessage(null);
    sync.mutate(serviceId ? { serviceId } : {}, {
      onSuccess: (data) => {
        const ok = data.results.filter((r) => r.status === 'ok');
        const failed = data.results.filter((r) => r.status === 'failed');
        setMessage(
          failed.length > 0
            ? `Синхронизация завершена с ошибками. ${failed
                .map((r) => `${r.serviceName}: ${r.error ?? 'причина не указана'}`)
                .join('. ')}`
            : ok.length === 0
              ? 'Синхронизировать нечего: проверьте ключи и включённые сервисы'
              : `Готово. Обновлено ${ok.length} ${plural(ok.length, 'сервис', 'сервиса', 'сервисов')}`,
        );
      },
      onError: (error) => {
        setMessage(error instanceof ApiError ? error.message : 'Синхронизация не удалась');
      },
    });
  }

  if (integrations.isLoading || integrations.isError) {
    return <QueryState query={integrations} what="состояние источников" />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <p
          style={{
            font: '400 13px/1.5 var(--font-text)',
            color: 'var(--text-muted)',
            maxWidth: 620,
          }}
        >
          {integrations.data?.schedulerEnabled
            ? 'Данные обновляются автоматически раз в час. Отсюда можно запустить сбор немедленно.'
            : 'Автоматическое обновление выключено в этой среде: данные собираются только по кнопке.'}{' '}
          Новые типы интеграций добавляются кодом, а не через этот экран.
        </p>
        <Button
          variant="secondary"
          onClick={() => runSync()}
          loading={sync.isPending}
          iconLeft={<Icon name="RefreshCw" size={16} />}
        >
          Синхронизировать всё
        </Button>
      </div>

      {message ? (
        <div style={{ font: '400 13px var(--font-text)', color: 'var(--text-muted)' }}>{message}</div>
      ) : null}

      <RatesCard />
      <WalletSourcesCard wallets={integrations.data?.wallets ?? []} />

      {withServices.length === 0 ? (
        <EmptyState
          icon="Plug"
          title="Источники ещё не подключены"
          description="Заведите сервис в разделе «Сервисы», задайте ключ доступа, и его состояние появится здесь: когда синхронизировался, чем закончилось, какая была ошибка."
        />
      ) : (
        withServices.map((row) => (
          <Card key={row.provider} padding="md">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div>
                <div
                  style={{
                    font: '500 16px var(--font-display)',
                    letterSpacing: 'var(--ls-heading)',
                    color: 'var(--text-strong)',
                  }}
                >
                  {row.title}
                </div>
                <Provenance>
                  {row.lastSuccessAt
                    ? `Последний успешный сбор ${formatRelative(row.lastSuccessAt)}`
                    : 'Успешных запусков ещё не было'}
                  {row.failureStreak > 0
                    ? `. Неудач подряд: ${row.failureStreak}`
                    : ''}
                </Provenance>
              </div>
              {statusBadge(row)}
            </div>

            <div
              style={{
                marginTop: 'var(--space-5)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-4)',
              }}
            >
              {row.services.map((service) => (
                <ServiceState
                  key={service.id}
                  service={service}
                  row={row}
                  busy={sync.isPending || backfill.isPending}
                  onOpen={() => navigate(`/finance/${service.id}`)}
                  onSync={() => runSync(service.id)}
                  onBackfill={() => {
                    setMessage(null);
                    setBackfillFor({ id: service.id, name: service.name });
                  }}
                />
              ))}
            </div>
          </Card>
        ))
      )}

      <Dialog
        open={backfillFor !== null}
        title="Импортировать историю"
        description={
          backfillFor
            ? `Приложение заберёт прошлые данные «${backfillFor.name}» и заполнит ими графики. Ограничения провайдера обходятся разбиением на части.`
            : ''
        }
        onClose={() => setBackfillFor(null)}
        width={520}
        footer={
          <>
            <Button variant="secondary" size={size} onClick={() => setBackfillFor(null)}>
              Отмена
            </Button>
            <Button
              size={size}
              loading={backfill.isPending}
              onClick={() => {
                if (!backfillFor) return;
                backfill.mutate(
                  { serviceId: backfillFor.id, days: Number(depth) },
                  {
                    onSuccess: (data) => {
                      setBackfillFor(null);
                      setMessage(
                        data.status === 'ok'
                          ? `История за ${data.days} дней загружена: записей расхода ${data.usageRecords}`
                          : `Импорт не завершён: ${data.error ?? 'причина неизвестна'}`,
                      );
                    },
                    onError: (error) => {
                      setBackfillFor(null);
                      setMessage(error instanceof ApiError ? error.message : 'Импорт не удался');
                    },
                  },
                );
              }}
            >
              Импортировать
            </Button>
          </>
        }
      >
        <Select
          size={size}
          label="Глубина импорта"
          options={DEPTH_OPTIONS}
          value={depth}
          onChange={(e) => setDepth(e.target.value)}
          help="Чем глубже, тем дольше идёт сбор. Повторный импорт не создаёт дублей"
        />
      </Dialog>
    </div>
  );
}
